import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { InfoCircleIcon } from "@cmsgov/ds-medicare-gov";
import { ActionType, IRAInsulinCoachmarkType } from "../../@types";
import { AppContext } from "../../app/store";
import AlertMessage from "../AlertMessage/AlertMessage";
import { AlertMessageProps } from "../AlertMessage/AlertMessage";
import { displayDrug, useTranslate } from "../../helpers";
import { useLocation } from "react-router";
import routes from "../../app/routes";
import {
  AnalyticsActionType,
  Ga4Event,
  Ga4EventType,
  Ga4ParentComponentType,
} from "../../app/contexts/Analytics/types";

interface InsulinCoachmarkProps
  extends Omit<AlertMessageProps, "icon" | "onClose"> {
  coachmarkType: IRAInsulinCoachmarkType;
}
export const InsulinCoachmark: FunctionComponent<InsulinCoachmarkProps> = ({
  coachmarkType,
  ...remainingProps
}: InsulinCoachmarkProps) => {
  const {
    state,
    state: { prescriptions, year, trackedEvents },
    dispatch,
  } = useContext(AppContext);
  const location = useLocation();
  const [insulinDrugList, setInsulinDrugList] = useState<string[]>([]);
  const flags = useFlags();
  const t = useTranslate();

  useEffect(() => {
    const trackedIraInsulinImpressions =
      trackedEvents[Ga4Event.IRA_INSULIN_IMPRESSION] || {};
    const insulinDrugs = prescriptions.filter(drug => drug.insulin);
    const drugsNotTrackedMap = {};

    if (insulinDrugs.length > 0) {
      insulinDrugs.forEach(drug => {
        const drugName = displayDrug(t, drug);
        if (!trackedIraInsulinImpressions[drugName]) {
          drugsNotTrackedMap[drugName] = true;
        }
      });
      const drugsNotTracked = Object.keys(drugsNotTrackedMap);
      if (drugsNotTracked.length > 0) {
        const drugNames = drugsNotTracked.join(",");
        dispatch({
          type: AnalyticsActionType.SEND_GA4_EVENT,
          settings: {
            event_name: Ga4Event.IRA_INSULIN_IMPRESSION,
            event_type: Ga4EventType.NON_INTERACTION,
            mct_insulin_drug_name: drugNames,
            parent_component_heading: "ira coach mark",
            parent_component_type: Ga4ParentComponentType.ALERT,
          },
        });
        dispatch({
          type: AnalyticsActionType.SEND_TEALIUM_EVENT,
          settings: {
            event_action: "ira insulin impression",
            event_label: drugNames,
          },
        });
        dispatch({
          type: ActionType.EVENT_TRACKED,
          payload: {
            event: Ga4Event.IRA_INSULIN_IMPRESSION,
            value: {
              ...trackedIraInsulinImpressions,
              ...drugsNotTrackedMap,
            },
          },
        });
      }
    }
    setInsulinDrugList(insulinDrugs.map(drug => displayDrug(t, drug)));
  }, [prescriptions]);

  const handleOnClose = () => {
    dispatch({
      type: ActionType.UPDATE_IRA_INSULIN_COACHMARK_HIDDEN_STATES,
      payload: coachmarkType,
    });
    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.IRA_INSULIN_ALERT_CLOSED,
        event_type: Ga4EventType.UI_INTERACTION,
        mct_insulin_drug_name: insulinDrugList.join(","),
        parent_component_heading: "ira coach mark",
        parent_component_type: Ga4ParentComponentType.ALERT,
      },
    });
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: "ira insulin alert closed",
        event_label: insulinDrugList.join(","),
      },
    });
  };
  const isSummaryPage = location.pathname === routes.summary.landingPage;
  const showInsulinCoachmark =
    insulinDrugList.length > 0 &&
    flags.tmpFeEnableIraInsulinCoachmarks &&
    (isSummaryPage || year === "2023") &&
    !state.iraInsulinCoachmarkHiddenStates[coachmarkType];

  return showInsulinCoachmark ? (
    <AlertMessage
      icon={<InfoCircleIcon />}
      onClose={handleOnClose}
      {...remainingProps}
    />
  ) : null;
};
